import {Location} from '@angular/common';
import {Inject, Injectable, Injector} from '@angular/core';
import {MsalBroadcastService, MsalService, MSAL_INSTANCE} from '@azure/msal-angular';
import {
    AuthenticationResult,
    InteractionRequiredAuthError,
    InteractionStatus,
    IPublicClientApplication,
    RedirectRequest,
    SilentRequest,
} from '@azure/msal-browser';
import {EMPTY, from, Observable, switchMap, take, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {LanguageService} from 'src/app/core/i18n/language.service';

export const getQueryParam = (params, url) => {
    let href = url;
    //this expression is to get the query strings
    let reg = new RegExp('[?&]' + params + '=([^&#]*)', 'i');
    let queryString = reg.exec(href);
    return queryString ? queryString[1] : null;
};

@Injectable()
export class ExtendedMsalService extends MsalService {

    constructor(
        @Inject(MSAL_INSTANCE) msalInstance: IPublicClientApplication,
        location: Location,
        injector: Injector,
        private languageService: LanguageService,
        private readonly broadcastService: MsalBroadcastService,
    ) {
        super(msalInstance, location, injector);
    }

    public loginRedirect(request?: RedirectRequest): Observable<void> {
        const nextRequest: RedirectRequest = (request ?? {scopes: ['openid']}) as RedirectRequest;
        const initExtraQueryParams = nextRequest.extraQueryParameters;

        const language = this.languageService.currentLang;
        const parameters: {ui_locales: string, idps?: string} = {ui_locales: language};
        const idpsParam = getQueryParam('idps', nextRequest.redirectStartPage);
        const langParam = getQueryParam('lang', nextRequest.redirectStartPage);
        if (idpsParam) {
            parameters.idps = idpsParam;
        }
        if (langParam) {
            parameters.ui_locales = langParam;
        }
        nextRequest.extraQueryParameters = {...parameters, ...(initExtraQueryParams ?? {})};
        return from(this.instance.loginRedirect(nextRequest));
    }

    /**
     * MsalInterceptor calls this to renew the access token attached to outgoing requests.
     * Its private fallback (`acquireTokenInteractively`) reacts to ANY failure here by
     * redirecting straight to the B2C login page and can spin forever if that redirect
     * races another in-flight interaction. We intercept here - the last public extension
     * point before that private fallback - so a genuinely dead session (refresh token
     * expired/revoked) bounces the user through a normal logout back to the portal instead.
     * Errors that aren't InteractionRequiredAuthError (network blips, iframe timeouts) are
     * left untouched so MSAL's built-in recovery still applies.
     */
    public override acquireTokenSilent(silentRequest: SilentRequest): Observable<AuthenticationResult> {
        return super.acquireTokenSilent(silentRequest).pipe(
            catchError((error) => {
                if (!(error instanceof InteractionRequiredAuthError)) {
                    return throwError(() => error);
                }

                return this.broadcastService.inProgress$.pipe(
                    take(1),
                    switchMap((status) => {
                        if (status !== InteractionStatus.None) {
                            return throwError(() => error);
                        }
                        return this.logoutRedirect({postLogoutRedirectUri: window.location.href}).pipe(
                            switchMap(() => EMPTY),
                        );
                    }),
                );
            }),
        );
    }
}